import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

type Theme = 'light' | 'dark';

const ThemeContext = createContext<{ theme: Theme; toggle: () => void }>({ theme: 'light', toggle: () => {} });

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem('sansara-theme');
    if (stored === 'dark' || stored === 'light') return stored;
    // Default new visitors to the warm light (cream) theme for a calm, relaxing
    // first impression — not the OS dark preference. Users can switch to dark and
    // the choice persists.
    return 'light';
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('sansara-theme', theme);
    // Keep the browser/OS chrome (address bar, iOS status bar) in step with the app.
    // index.html cannot do this with a `media="(prefers-color-scheme: …)"` meta any
    // more: the theme is class-driven from localStorage and defaults to light, so a
    // CSS media query — which cannot see localStorage — would paint dark chrome above
    // a cream app for every dark-OS visitor. These are the body backgrounds
    // (bg-surface-sunken / dark:bg-surface-dark-sunken).
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', theme === 'dark' ? '#17140e' : '#f3ecdd');
  }, [theme]);

  const value = useMemo(
    () => ({ theme, toggle: () => setTheme((t) => (t === 'light' ? 'dark' : 'light')) }),
    [theme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
